import { useCallback, useEffect, useRef } from 'react';
import { useBlocker, useLocation } from 'react-router-dom';

interface SettingsRouteGuardOptions {
  dirty: boolean;
  busy: boolean;
  requestLeave: (action: () => void) => void;
  onLocationChange?: () => void;
}

/** Browser Back and app links follow the same rules as the settings close button. */
export function useSettingsRouteGuard({
  dirty,
  busy,
  requestLeave,
  onLocationChange,
}: SettingsRouteGuardOptions) {
  const blocker = useBlocker(dirty || busy);
  const location = useLocation();
  const previousLocation = useRef(location.key);
  const requestedLocation = useRef<string | null>(null);

  useEffect(() => {
    if (blocker.state !== 'blocked') {
      requestedLocation.current = null;
      return;
    }
    if (busy) {
      blocker.reset();
      return;
    }
    if (requestedLocation.current === blocker.location.key) return;
    requestedLocation.current = blocker.location.key;
    requestLeave(blocker.proceed);
  }, [blocker, busy, requestLeave]);

  useEffect(() => {
    if (previousLocation.current === location.key) return;
    previousLocation.current = location.key;
    onLocationChange?.();
  }, [location.key, onLocationChange]);

  const resetBlockedNavigation = useCallback(() => {
    requestedLocation.current = null;
    if (blocker.state === 'blocked') blocker.reset();
  }, [blocker]);

  return { resetBlockedNavigation };
}
